// src/components/manager-v2/flows/TeamWorkflowList.js
import React, { useEffect } from 'react';
import {
    GitBranch, Play, Pause, Clock, CheckCircle
} from 'lucide-react';
import { useFlowAutomation } from '../../../contexts/FlowAutomationContext';
import { useTheme } from '../../../contexts/ThemeContext';
import flowAutomationService from '../../../services/flowAutomation.service';

const StatusBadge = ({ status }) => {
    const styles = {
        draft: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
        active: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
        paused: 'bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400',
    };
    return (
        <span className={`text-xs px-2 py-0.5 rounded-full font-medium capitalize ${styles[status] || styles.draft}`}>
            {status}
        </span>
    );
};

const TeamWorkflowList = () => {
    const { theme } = useTheme();
    const isDark = theme === 'dark';
    const { workflows, fetchWorkflows, toggleStatus, loading } = useFlowAutomation();

    useEffect(() => {
        // Fetch team-scoped workflows
        const loadTeamWorkflows = async () => {
            try {
                const result = await flowAutomationService.getTeamWorkflows();
                if (result.workflows) {
                    // We can set these via context if needed — for now we use the general fetch
                }
            } catch (err) {
                console.error('Error loading team workflows:', err);
            }
        };
        fetchWorkflows({ scope: 'team' });
    }, [fetchWorkflows]);

    const teamWorkflows = workflows.filter(w => w.scope === 'team');

    if (!loading && teamWorkflows.length === 0) {
        return (
            <div className={`flex flex-col items-center justify-center py-16 rounded-xl border-2 border-dashed ${
                isDark ? 'border-gray-700 bg-gray-800/30' : 'border-gray-200 bg-gray-50/50'
            }`}>
                <GitBranch className={`w-10 h-10 mb-3 ${isDark ? 'text-gray-600' : 'text-gray-300'}`} />
                <h3 className={`text-lg font-semibold mb-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    No team workflows
                </h3>
                <p className={`text-sm max-w-sm text-center ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    Team workflows created by admins will appear here. You can also create quick automation rules for your team.
                </p>
            </div>
        );
    }

    return (
        <div className="space-y-3">
            {teamWorkflows.map((wf) => (
                <div
                    key={wf.id}
                    className={`flex items-center justify-between p-4 rounded-xl border ${
                        isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
                    } hover:shadow-sm transition-shadow`}
                >
                    <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-lg ${isDark ? 'bg-gray-700' : 'bg-gray-100'}`}>
                            <GitBranch className={`w-5 h-5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
                        </div>
                        <div>
                            <p className={`font-medium text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                {wf.name}
                            </p>
                            <div className="flex items-center gap-3 mt-0.5">
                                <span className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                                    {wf.steps?.length || 0} steps
                                </span>
                                <span className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                                    {wf.stats?.runs ?? 0} runs
                                </span>
                                {wf.stats?.last_run && (
                                    <span className={`text-xs flex items-center gap-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                                        <Clock className="w-3 h-3" />
                                        Last: {new Date(wf.stats.last_run).toLocaleDateString()}
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="flex items-center gap-3">
                        <StatusBadge status={wf.status} />
                        {wf.status === 'active' ? (
                            <button
                                onClick={() => toggleStatus(wf.id, 'paused')}
                                className={`p-1.5 rounded-lg ${isDark ? 'hover:bg-gray-700 text-amber-400' : 'hover:bg-gray-100 text-amber-500'}`}
                                title="Pause"
                            >
                                <Pause className="w-4 h-4" />
                            </button>
                        ) : wf.status === 'paused' ? (
                            <button
                                onClick={() => toggleStatus(wf.id, 'active')}
                                className={`p-1.5 rounded-lg ${isDark ? 'hover:bg-gray-700 text-green-400' : 'hover:bg-gray-100 text-green-500'}`}
                                title="Activate"
                            >
                                <Play className="w-4 h-4" />
                            </button>
                        ) : null}
                    </div>
                </div>
            ))}
        </div>
    );
};

export default TeamWorkflowList;
